import { useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import type {
  CandidateSourceApi,
  CandidateStatusApi,
} from '@/modules/candidates/types';

interface CandidateStatusTagProps {
  label?: string;
  status: CandidateStatusApi;
  source: CandidateSourceApi;
  unifiedPrescreeningStatus: boolean;
}

const STATUS_LABELS: Record<CandidateStatusApi, string> = {
  New: 'NEW',
  Rejected: 'REJECTED',
  Shortlisted: 'SHORTLISTED',
  Declined: 'DECLINED',
  Contacted: 'CONTACTED',
  Prescreened: 'PRESCREENED',
  PrescreeningScheduled: 'PRESCREENING SCHEDULED',
  Submitted: 'SUBMITTED',
  AiPrescreened: 'AI PRESCREENED',
  Interviewing: 'INTERVIEWING',
  Hired: 'HIRED',
  ContactStarted: 'CONTACT STARTED',
  AssessmentCompleted: 'ASSESSMENT COMPLETED',
  ReadyForSubmission: 'READY FOR SUBMISSION',
  HiringManagerApproved: 'HM APPROVED',
};

const STATUS_STYLES: Record<CandidateStatusApi, string> = {
  New: 'border-neutral-400 text-neutral-650 bg-transparent',
  Rejected: 'border-semantic-550 bg-semantic-550/10 text-semantic-550',
  Shortlisted: 'border-yellow-500 bg-yellow-50 text-yellow-700',
  Declined: 'border-semantic-550 bg-semantic-550/10 text-semantic-550',
  Contacted: 'border-blue-500 bg-blue-50 text-blue-600',
  Prescreened: 'border-primary-600 bg-primary-600/10 text-primary-600',
  PrescreeningScheduled:
    'border-primary-600 bg-primary-600/10 text-primary-600',
  Submitted: 'border-green-600 bg-green-50 text-green-700',
  AiPrescreened: 'border-primary-600 bg-primary-600/10 text-primary-600',
  Interviewing: 'border-primary-600 bg-primary-600/10 text-primary-600',
  Hired: 'border-primary-600 bg-primary-600/10 text-primary-600',
  ContactStarted: 'border-primary-600 bg-primary-600/10 text-primary-600',
  AssessmentCompleted: 'border-violet-500 bg-violet-50 text-violet-700',
  ReadyForSubmission: 'border-violet-500 bg-violet-50 text-violet-700',
  HiringManagerApproved: 'border-green-600 bg-green-50 text-green-700',
};

const NUMERIC_STATUS_MAP: Record<number, CandidateStatusApi> = {
  0: 'New',
  1: 'Rejected',
  2: 'Shortlisted',
  3: 'Declined',
  4: 'Contacted',
  5: 'PrescreeningScheduled',
  6: 'Prescreened',
  7: 'Submitted',
  8: 'AiPrescreened',
  12: 'AssessmentCompleted',
  13: 'ReadyForSubmission',
  14: 'HiringManagerApproved',
};

const normalizeStatus = (status: unknown): CandidateStatusApi | null => {
  if (typeof status === 'string' && status in STATUS_LABELS) {
    return status as CandidateStatusApi;
  }

  if (typeof status === 'number') {
    return NUMERIC_STATUS_MAP[status] ?? null;
  }

  return null;
};

const CandidateStatusTag = ({
  label,
  status,
  source,
  unifiedPrescreeningStatus,
}: CandidateStatusTagProps) => {
  const [showInfo, setShowInfo] = useState(false);
  const normalizedStatus = normalizeStatus(status);

  const getStatusStyles = () => {
    if (showInfo) {
      return 'border-neutral-400 text-neutral-650 bg-transparent';
    }

    return normalizedStatus
      ? STATUS_STYLES[normalizedStatus]
      : 'border-neutral-400 text-neutral-650 bg-transparent';
  };

  const getDisplayLabel = () => {
    if (label) {
      return label;
    }

    if (!normalizedStatus) {
      return 'UNKNOWN';
    }

    if (
      unifiedPrescreeningStatus &&
      (normalizedStatus === 'Prescreened' ||
        normalizedStatus === 'AiPrescreened')
    ) {
      return 'PRESCREENED';
    }

    return STATUS_LABELS[normalizedStatus];
  };

  const displayContent = showInfo ? (
    <>
      {source === 'ManualUpload' && 'Manual CV upload'}
      {source === 'Coresignal' && 'AI sourced'}
    </>
  ) : (
    getDisplayLabel()
  );

  return (
    <button
      type="button"
      onClick={() => setShowInfo(!showInfo)}
      className={cn(
        'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
        getStatusStyles(),
      )}
    >
      {displayContent}
    </button>
  );
};

export default CandidateStatusTag;
